import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import type {Document} from '@contentful/rich-text-types'
import {BLOCKS} from '@contentful/rich-text-types'
import {Box, Grid, Pagination, Text, ThemeProvider} from '@primer/react-brand'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Aside} from './components/Aside'
import {ResourceCard} from './components/Card'
import type {IndexPage} from '../../../schemas/contentful/contentTypes/indexTemplate'
import {ContentfulHero} from '../../../components/contentful/ContentfulHero/ContentfulHero'
import {appendFeatureFlagsToUrl} from '../../../lib/utils/urls'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'

interface ResourceSummary {
  heading?: string | null
  image?: {
    description?: string | null
    url?: string | null
  } | null
  path?: string | null
  description?: Document | null
}

interface Topic {
  id: string
  preferred_label: string
  definition?: string
}

interface DynamicIndexTemplateProps {
  page: IndexPage
  topics: Topic[]
  resources?: ResourceSummary[]
  additionalProps?: {
    topic_ids?: string[]
    totalPages?: number
    sortOptions?: Array<{label: string; value: string}>
    currentSort?: string
  }
}

export function DynamicIndexTemplate({topics, resources, additionalProps, page}: DynamicIndexTemplateProps) {
  const {hero, emptyStateDescription} = page.fields.template.fields
  const topicIds = additionalProps?.topic_ids || []
  const totalPages = additionalProps?.totalPages ?? 0
  const colorMode = page.fields.settings?.fields.colorMode ?? 'light'

  const pageParam = new URLSearchParams(ssrSafeLocation.search).get('page')
  const parsedPage = Number(pageParam)
  const currentPage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1

  const getPaginationURL = (pageNumber: number): string => {
    const url = new URL(`${ssrSafeLocation.pathname}${ssrSafeLocation.search}`, ssrSafeLocation.origin)
    const queryParams = new URLSearchParams(ssrSafeLocation.search)
    const featureFlags = queryParams.get('_features') || ''

    url.searchParams.delete('_features')
    url.searchParams.set('page', pageNumber.toString())
    return appendFeatureFlagsToUrl(url.toString(), featureFlags)
  }

  const emptyState = (
    <Box role="status">
      {emptyStateDescription
        ? documentToReactComponents(emptyStateDescription, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => (
                <Text variant="muted" as="p">
                  {children}
                </Text>
              ),
            },
          })
        : 'No results found. Please clear your filters and try another search.'}
    </Box>
  )

  const cards = (resources || []).map((resource, index) => {
    if (!resource.path || !resource.heading) return null

    return (
      <Grid.Column span={{xsmall: 12, medium: 6}} key={resource.path || index}>
        <ResourceCard resource={resource} index={index} colorMode={colorMode} />
      </Grid.Column>
    )
  })

  return (
    <ThemeProvider colorMode={colorMode}>
      <Box>
        <Grid data-testid="dynamic-index-main-grid">
          <Grid.Column span={12}>
            <Box marginBlockStart={{narrow: 64, regular: 112}} marginBlockEnd={{regular: 64}}>
              {hero ? (
                <ContentfulHero data-hpc component={hero} heroClassName="pt-0 pb-0" paddingBlockEnd="none" />
              ) : null}
            </Box>
          </Grid.Column>

          <Grid.Column span={{xsmall: 12, medium: 3}}>
            <Aside topics={topics} selectedTopicIds={topicIds} />
          </Grid.Column>

          <Grid.Column span={{xsmall: 12, medium: 9}}>
            {(resources || []).length > 0 ? <Grid data-testid="dynamic-index-cards-grid">{cards}</Grid> : emptyState}
            {totalPages > 1 ? (
              <Box
                paddingBlockStart={28}
                marginBlockStart={48}
                borderBlockStartWidth="thin"
                borderColor="default"
                borderStyle="solid"
              >
                <Pagination
                  pageCount={totalPages}
                  currentPage={currentPage}
                  hrefBuilder={getPaginationURL}
                  pageAttributesBuilder={nextPageNumber => {
                    return getAnalyticsEvent({
                      action: `page_${nextPageNumber}`,
                      tag: 'link',
                      context: 'pagination',
                      location: 'dynamic_index_cards',
                    })
                  }}
                  showPages
                />
              </Box>
            ) : null}
          </Grid.Column>
        </Grid>
      </Box>
    </ThemeProvider>
  )
}
